import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import { ModernButton, ModernCard, ModernCheckbox } from './ModernComponents';
import './styles/step_crops.style.scss';

const CultureRow = ({
  culture,
  isSelected,
  isFixed,
  cultureMinMax,
  toggleCultureSelection,
  updateCultureMinMax,
  totalSurface,
  t,
}) => (
  <ModernCheckbox
    key={culture.name}
    label={
      <span className="cultureLabel">
        {culture.name}
        {isFixed && (
          <span className="cultureFixed" title={t('simulation.step.cultures.fixedCultureTitle')}>
            *
          </span>
        )}
      </span>
    }
    checked={isSelected}
    disabled={isFixed}
    inputType={'minmaxWithUnit'}
    constraintName={culture.id}
    constraintValues={cultureMinMax[culture.id]}
    totalSurface={totalSurface}
    onUpdateConstraintValue={updateCultureMinMax}
    onChange={() => toggleCultureSelection(culture.id)}
  />
);

const StepCrops = ({
  searchTerm,
  setSearchTerm,
  filteredCultures,
  selectedCultures,
  setSelectedCultures,
  cultureMinMax,
  setCultureMinMax,
  toggleCultureSelection,
  updateCultureMinMax,
  totalSurface = 0,
  fixedCultureIds = [],
}) => {
  const { t } = useTranslation();
  const {
    conventionalCultures,
    organicCultures,
    intermediateConventionalCultures,
    intermediateOrganicCultures,
    hasConventional,
    hasOrganic,
    hasBoth,
    hasIntermediateConventional,
    hasIntermediateOrganic,
  } = useMemo(() => {
    const main = filteredCultures.filter((c) => c.cultureType !== 'intermediate');
    const intermediate = filteredCultures.filter((c) => c.cultureType === 'intermediate');

    const conventional = main.filter(
      (c) => c.isOrganic === 'conventional' || c.isOrganic === 'both',
    );
    const organic = main.filter((c) => c.isOrganic === 'organic');

    const cropsInterConventional = intermediate.filter(
      (c) => c.isOrganic === 'conventional' || c.isOrganic === 'both',
    );
    const cropsInterOrganic = intermediate.filter((c) => c.isOrganic === 'organic');

    const allTypes = main.map((c) => c.isOrganic);
    const hasConv = allTypes.some((t) => t === 'conventional' || t === 'both');
    const hasOrg = allTypes.some((t) => t === 'organic');
    const hasBothType = allTypes.some((t) => t === 'both');

    return {
      conventionalCultures: conventional,
      organicCultures: organic,
      intermediateConventionalCultures: cropsInterConventional,
      intermediateOrganicCultures: cropsInterOrganic,
      hasConventional: hasConv,
      hasOrganic: hasOrg,
      hasBoth: hasBothType,
      hasIntermediateConventional: cropsInterConventional.length > 0,
      hasIntermediateOrganic: cropsInterOrganic.length > 0,
    };
  }, [filteredCultures]);

  const conventionalTitle = hasBoth
    ? t('simulation.step.cultures.conventionalMixed')
    : t('simulation.step.cultures.conventional');

  const isAllSelected = selectedCultures.length === filteredCultures?.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCultures([]);
    } else {
      setSelectedCultures(filteredCultures?.map((p) => p.id));
      const missingMinMax = Object.fromEntries(
        filteredCultures
          ?.filter((c) => !cultureMinMax[c.id])
          .map((c) => [c.id, { min: 0, max: 0, unit: 'ha' }]) ?? [],
      );
      setCultureMinMax({ ...cultureMinMax, ...missingMinMax });
    }
  };

  return (
    <div className="stepCrops">
      <ModernCard
        title={t('simulation.step.cultures.title')}
        icon={AgricultureIcon}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t('common.cultures')}
        selectedCulture={selectedCultures.length}
        headerLeft={
          <ModernButton variant="secondary" onClick={handleSelectAll}>
            {isAllSelected ? t('simulation.common.deselectAll') : t('common.selectAll')}
          </ModernButton>
        }
      >
        <div className="culturesWrapper">
          {hasConventional && (
            <div className="cultureGroup">
              <h3>{conventionalTitle}</h3>
              <div className="cultureList">
                {conventionalCultures.map((culture) => (
                  <CultureRow
                    key={culture.id}
                    culture={culture}
                    isSelected={selectedCultures.includes(culture.id)}
                    isFixed={fixedCultureIds.includes(culture.id)}
                    cultureMinMax={cultureMinMax}
                    toggleCultureSelection={toggleCultureSelection}
                    updateCultureMinMax={updateCultureMinMax}
                    totalSurface={totalSurface}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {hasOrganic && (
            <div className="cultureGroup">
              {<h3>{t('simulation.step.cultures.organic')}</h3>}
              <div className="cultureList">
                {organicCultures.map((culture) => (
                  <CultureRow
                    key={culture.id}
                    culture={culture}
                    isSelected={selectedCultures.includes(culture.id)}
                    isFixed={fixedCultureIds.includes(culture.id)}
                    cultureMinMax={cultureMinMax}
                    toggleCultureSelection={toggleCultureSelection}
                    updateCultureMinMax={updateCultureMinMax}
                    totalSurface={totalSurface}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {hasIntermediateConventional && (
            <div className="cultureGroup">
              <h3>
                {hasBoth
                  ? t('simulation.step.cultures.intermediateConventionalMixed')
                  : t('simulation.step.cultures.intermediateConventional')}
              </h3>
              <div className="cultureList">
                {intermediateConventionalCultures.map((culture) => (
                  <CultureRow
                    key={culture.id}
                    culture={culture}
                    isSelected={selectedCultures.includes(culture.id)}
                    isFixed={fixedCultureIds.includes(culture.id)}
                    cultureMinMax={cultureMinMax}
                    toggleCultureSelection={toggleCultureSelection}
                    updateCultureMinMax={updateCultureMinMax}
                    totalSurface={totalSurface}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {hasIntermediateOrganic && (
            <div className="cultureGroup">
              <h3>{t('simulation.step.cultures.intermediateOrganic')}</h3>
              <div className="cultureList">
                {intermediateOrganicCultures.map((culture) => (
                  <CultureRow
                    key={culture.id}
                    culture={culture}
                    isSelected={selectedCultures.includes(culture.id)}
                    isFixed={fixedCultureIds.includes(culture.id)}
                    cultureMinMax={cultureMinMax}
                    toggleCultureSelection={toggleCultureSelection}
                    updateCultureMinMax={updateCultureMinMax}
                    totalSurface={totalSurface}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={`infoBadge infoBadgeSuccess`}>
          <p>
            <strong>{selectedCultures.length}</strong> {t('simulation.step.cultures.selectedCount')}
          </p>
        </div>

        {fixedCultureIds.length > 0 && (
          <div className="infoBadge">
            <p>{t('simulation.step.cultures.fixedCultureInfo')}</p>
          </div>
        )}
      </ModernCard>
    </div>
  );
};

export default StepCrops;
